import { ReactNode, useEffect, useState } from "react";
import useUnleashFeatureFlagAdapter from "../../adapter/UnleashFeatureFlagAdapter";

// ponytail: session-long cache, no TTL. Flags don't change mid-session, and the
// popup remounts these components on every open — without this each open refetches.
// Reload picks up flag changes; add TTL/invalidation only if that proves insufficient.
const flagCache = new Map<string, boolean>();

export const clearFeatureFlagCache = () => flagCache.clear();

const useFeatureFlagWithContext = (
  featureFlag: string,
  context: Record<string, string | number>
) => {
  const { rolloutReleaseByContextDirectly, flagsReady } =
    useUnleashFeatureFlagAdapter();
  const cacheKey =
    featureFlag && context ? `${featureFlag}::${JSON.stringify(context)}` : "";
  const [isFeatureFlagEnable, setIsFeatureFlagEnable] = useState<boolean>(
    flagCache.get(cacheKey) ?? false
  );
  const [flagResolved, setFlagResolved] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    setFlagResolved(false);

    const checkFlag = async () => {
      try {
        const enabled = await rolloutReleaseByContextDirectly(
          featureFlag,
          context
        );
        flagCache.set(cacheKey, enabled);
        if (mounted) setIsFeatureFlagEnable(enabled);
      } catch (error) {
        console.error(
          `[useFeatureFlagWithContext] Failed to check flag: ${featureFlag}`,
          error
        );
        if (mounted) setIsFeatureFlagEnable(false);
      } finally {
        if (mounted) setFlagResolved(true);
      }
    };

    if (featureFlag && context) {
      const hasUndefinedValues = Object.values(context).some(
        value => value === undefined
      );

      if (hasUndefinedValues) {
        setIsFeatureFlagEnable(false);
        setFlagResolved(true);
        return;
      }

      if (flagCache.has(cacheKey)) {
        setIsFeatureFlagEnable(flagCache.get(cacheKey) as boolean);
        setFlagResolved(true);
        return;
      }

      checkFlag();
    } else {
      setIsFeatureFlagEnable(false);
      setFlagResolved(true);
    }

    return () => {
      mounted = false;
    };
  }, [featureFlag, JSON.stringify(context), rolloutReleaseByContextDirectly]);

  const handleRenderByFeatureFlag = (
    component: ReactNode,
    defaultComponent: ReactNode
  ): ReactNode => {
    return isFeatureFlagEnable ? component : defaultComponent;
  };

  return {
    handleRenderByFeatureFlag,
    isFeatureFlagEnable,
    flagsReady,
    flagResolved,
  };
};

export default useFeatureFlagWithContext;
